import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { ErrorMessageResponse, ErrorType } from '@/types';
import { ErrorMap } from '@/constants/errors';


export class ErrorResponse {
  private error: AxiosError;
  constructor(error: unknown) {
    this.error = error as AxiosError;
  }

  getStatus = () => {
    return this.error.status;
  };

  getType = () => {
    return this.getStatus()
      ? ErrorMap[this.getStatus() as keyof typeof ErrorMap] ||
          ErrorType.UnknownError
      : undefined;
  };

  getMessage = () => {
    return this.error.message;
  };

  getResponseMessage = () => {
    return (this.error.response?.data as ErrorMessageResponse)?.message;
  };

  /**
   * Der ganze Antwort-Body — fuer Endpunkte, die neben `message` ein
   * fachliches Flag mitgeben (z. B. `fall_bereits_gewechselt` bei der
   * Termin-Umverteilung). Bewusst generisch und unveraendert durchgereicht.
   */
  getResponseData = <T = Record<string, unknown>>(): T | undefined => {
    return this.error.response?.data as T | undefined;
  };
}

/**
 * Loest den Fehlertyp fuer den Store auf, OHNE zu unterstellen, dass der
 * gefangene Wert eine ErrorResponse ist.
 *
 * Hintergrund (Sentry BERTATERWETL-FRONTEND-A, 28.07.): die Stores casten im
 * catch blind `(e as ErrorResponse).getType()`. Die API-Schicht wirft zwar
 * ueberall ErrorResponse — aber im try-Block steht mehr als der API-Aufruf.
 * Faellt dort ein gewoehnlicher Fehler an (TypeError beim Weiterverarbeiten der
 * Antwort), hat er kein getType, der Cast wirft "a.getType is not a function"
 * und die EIGENTLICHE Ursache ist weg. Genau das war auf customer-details zu
 * sehen: der echte Fehler stand nie in Sentry, nur der Folgefehler.
 *
 * Fremde Fehler werden deshalb als UnknownError abgebildet und zusaetzlich an
 * Sentry gemeldet — die Ursache soll sichtbar bleiben, nicht ihr Schatten.
 */
export const toErrorType = (error: unknown): ErrorType => {
  if (error instanceof ErrorResponse) {
    return error.getType() ?? ErrorType.UnknownError;
  }

  Sentry.captureException(error);

  return ErrorType.UnknownError;
};

/**
 * Speicherfehler eines einzelnen Feldes MIT fachlicher Meldung fuer den Nutzer
 * (Triage §7 E, 02.11.2026: „Die E-Mail-Adresse ist ungültig …" vom Backend).
 * `EditableField` zeigt nur die Meldung dieser Klasse — jeder andere Fehler
 * bleibt beim generischen „Speichern fehlgeschlagen" (keine Technik-Texte).
 */
export class FeldSpeicherFehler extends Error {}

/** Erste Validierungsmeldung des Backends zu `field` (422 `errors[field][0]`), sonst null. */
export const feldMeldungAus = (error: unknown, field: string): string | null => {
  if (!(error instanceof ErrorResponse) || error.getStatus() !== 422) return null;
  const daten = error.getResponseData<{ errors?: Record<string, unknown> }>();
  const liste = daten?.errors?.[field];
  return Array.isArray(liste) && typeof liste[0] === 'string' && liste[0] !== '' ? liste[0] : null;
};

/** Wirft fuer den Feld-Speicherpfad: mit Backend-Meldung als FeldSpeicherFehler, sonst generisch. */
export const feldSpeicherFehler = (error: unknown, field: string): Error => {
  const meldung = feldMeldungAus(error, field);
  return meldung ? new FeldSpeicherFehler(meldung) : new Error('Speichern fehlgeschlagen');
};

/**
 * Erste Validierungsmeldung eines 422 — gleich welches Feld (`errors[*][0]`),
 * sonst `message` des 422. Bei jedem anderen Status oder fremdem Fehler: null.
 *
 * Fuer Anzeigen, die ein ganzes Formular abschicken und nicht wissen, welches
 * Feld der Server beanstandet (Pool-Vorschau Schritt 7, Fall 158996 am
 * 03.11.2026: „Pool-Klassifikation fehlgeschlagen" verdeckte die eigentliche
 * Ursache, eine negative Partner-Einkunft).
 */
export const ersteValidierungsMeldung = (error: unknown): string | null => {
  if (!(error instanceof ErrorResponse) || error.getStatus() !== 422) return null;
  const daten = error.getResponseData<{ message?: unknown; errors?: Record<string, unknown> }>();
  for (const liste of Object.values(daten?.errors ?? {})) {
    if (Array.isArray(liste) && typeof liste[0] === 'string' && liste[0] !== '') return liste[0];
  }
  return typeof daten?.message === 'string' && daten.message !== '' ? daten.message : null;
};
